import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AUTH_SESSION } from '@prosaas/psw-events';
import {
  Alert,
  FormFieldTextInput,
  FullPageSpinner,
  IconEye,
  IconLockClosed,
  IconOffEye,
  NavLinkDisplay,
  Card,
  CardBodyWrapper,
  ColumnWrapper,
  CardTitle,
  Button,
} from '@prosaas/psw-styleguide';
import { skipEmptyPayload, subscribe } from '@prosaas/psw-utils';
import { apiService } from '../services/api.service';
import { FLEXIBLE_PASSWORD_REGEX } from '../utils/regexes';
import { homeRoute } from '../utils/routes';
import { authTestIds } from '../testIds.js';
import { useAuthTranslations } from '../hooks/useAuthTranslations';

type FormInputs = {
  password: string;
  repeatPassword: string;
  // TODO: handle "remember me" option
};

const ResetPasswordForm: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [apiError, setApiErr] = useState<null | { [key: string]: any }>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [translations, isLoadingTranslations] = useAuthTranslations();

  useEffect(() => {
    const unsubscriber = subscribe(
      AUTH_SESSION,
      skipEmptyPayload(({ isLoggedIn }) => {
        setIsLoggedIn(isLoggedIn);
      }),
    );

    return function cleanUp() {
      unsubscriber();
    };
  }, []);

  const {
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm<FormInputs>();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setApiErr(null);

    if (data.password !== data.repeatPassword) {
      setError('repeatPassword', {
        type: 'manual',
        message: 'Passwords do not match',
      });
      return;
    }

    setIsSending(true);

    try {
      await apiService.resetPassword({ password: data.password, token });
      navigate(homeRoute());
    } catch (error) {
      setApiErr(error);
    } finally {
      setIsSending(false);
    }
  };

  const CREATE_NEW_PASSWORD = translations.createNewPassword();
  const GENERIC_ERROR = translations.genericError();
  const PASSWORD = translations.password();
  const NEW_PASSWORD = translations.newPassword();
  const ENTER_YOUR_PASSWORD = translations.enterYourPassword();
  const INVALID_PASSWORD = translations.invalidPassword();
  const REPEAT_PASSWORD = translations.repeatPassword();
  const RETYPE_YOUR_PASSWORD = translations.retypeYourPassword();
  const SUBMIT_BUTTON = isSending ? translations.submitting() : translations.submit();
  const SIGN_IN_INSTEAD = translations.signInInstead();

  if (isLoadingTranslations) {
    return <FullPageSpinner />;
  }

  if (isLoggedIn) {
    navigate(homeRoute());
    return null;
  }

  return (
    <Card isRaised>
      <ColumnWrapper>
        <CardBodyWrapper>
          {isSending && <FullPageSpinner />}
          <CardTitle>{CREATE_NEW_PASSWORD}</CardTitle>
          {apiError && <Alert category="error">{apiError?.message || GENERIC_ERROR}</Alert>}
          <ColumnWrapper gapSize="sm">
            <Controller
              defaultValue=""
              control={control}
              name={'password'}
              rules={{
                required: ENTER_YOUR_PASSWORD,
                pattern: {
                  value: FLEXIBLE_PASSWORD_REGEX,
                  message: INVALID_PASSWORD,
                },
              }}
              render={({ field: { value, name, onChange } }) => (
                <FormFieldTextInput
                  name={name}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={PASSWORD}
                  autoComplete="currentPassword"
                  label={NEW_PASSWORD}
                  startIcon={<IconLockClosed />}
                  endIcon={
                    <>
                      {showPassword ? (
                        <IconEye onClick={() => setShowPassword(!showPassword)} />
                      ) : (
                        <IconOffEye onClick={() => setShowPassword(!showPassword)} />
                      )}
                    </>
                  }
                  disabled={isSending}
                  error={errors?.password?.message || null}
                  value={value}
                  onChange={onChange}
                  testId={authTestIds.loginPassword}
                />
              )}
            />
            <Controller
              defaultValue=""
              control={control}
              name={'repeatPassword'}
              rules={{
                required: RETYPE_YOUR_PASSWORD,
              }}
              render={({ field: { value, name, onChange } }) => (
                <FormFieldTextInput
                  name={name}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={REPEAT_PASSWORD}
                  label={REPEAT_PASSWORD}
                  autoComplete="currentPassword"
                  startIcon={<IconLockClosed />}
                  endIcon={
                    <>
                      {showPassword ? (
                        <IconEye onClick={() => setShowPassword(!showPassword)} />
                      ) : (
                        <IconOffEye onClick={() => setShowPassword(!showPassword)} />
                      )}
                    </>
                  }
                  disabled={isSending}
                  error={errors?.repeatPassword?.message || null}
                  value={value}
                  onChange={onChange}
                  testId={authTestIds.loginPassword}
                />
              )}
            />
            <Button
              disabled={!!errors?.password?.message || !!errors?.repeatPassword?.message || isSending}
              data-test-id={authTestIds.submitButton}
              onClick={handleSubmit(onSubmit)}
            >
              {SUBMIT_BUTTON}
            </Button>
          </ColumnWrapper>
        </CardBodyWrapper>
        <NavLink data-test-id={authTestIds.signInInstead} to="/">
          <NavLinkDisplay displayName={SIGN_IN_INSTEAD} />
        </NavLink>
      </ColumnWrapper>
    </Card>
  );
};

export default ResetPasswordForm;
